import React, { useState, useRef } from "react";
import { useGame } from "../game/em2c-store";
import { RESEARCH_BRANCHES, TECHS, isTechUnlocked } from "../game/data/research";
import { EM2C_RESOURCES as RESOURCES } from "../game/em2c-store";
import { X, FlaskConical } from "lucide-react";

// ─── Branch config ─────────────────────────────────────────────
const BRANCH_META = {
  engineering: { color: "#B042FF", glow: "#B042FF88", label: "Engineering", abbr: "ENG" },
  bio:         { color: "#00FA9A", glow: "#00FA9A88", label: "Bio Sciences", abbr: "BIO" },
  aerospace:   { color: "#00E5FF", glow: "#00E5FF88", label: "Aerospace",    abbr: "AER" },
};

// ─── SVG icon per tech ─────────────────────────────────────────
function SolarIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="7" fill={color} opacity="0.9"/>
      {[0,45,90,135,180,225,270,315].map((a,i) => {
        const r1=10, r2=16, rad=a*Math.PI/180;
        return <line key={i} x1={20+r1*Math.cos(rad)} y1={20+r1*Math.sin(rad)} x2={20+r2*Math.cos(rad)} y2={20+r2*Math.sin(rad)} stroke={color} strokeWidth="2.5" strokeLinecap="round"/>;
      })}
    </svg>
  );
}
function BatteryIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <rect x="11" y="10" width="18" height="22" rx="2" fill="none" stroke={color} strokeWidth="2.5"/>
      <rect x="16" y="6" width="8" height="4" rx="1" fill={color}/>
      <rect x="14" y="14" width="12" height="4" fill={color} opacity="0.9"/>
      <rect x="14" y="20" width="12" height="4" fill={color} opacity="0.6"/>
      <rect x="14" y="26" width="12" height="4" fill={color} opacity="0.3"/>
    </svg>
  );
}
function StorageIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      {[0,1,2].map(i => <rect key={i} x="8" y={9+i*9} width="24" height="6" rx="1.5" fill="none" stroke={color} strokeWidth="2" opacity={1-i*0.2}/>)}
      <line x1="14" y1="12" x2="26" y2="12" stroke={color} strokeWidth="1.5" opacity="0.5"/>
    </svg>
  );
}
function HeatIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M12 28 C12 20 18 22 18 14 C18 10 15 8 15 8 C22 10 24 16 24 20 C24 16 28 14 28 14 C28 22 28 28 20 32 C14 32 12 28 12 28 Z" fill={color} opacity="0.85"/>
      <path d="M18 28 C18 24 20 22 20 22 C22 24 22 26 22 28 Z" fill="#fff" opacity="0.7"/>
    </svg>
  );
}
function DroneIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="4" fill={color} opacity="0.9"/>
      {[[6,6],[34,6],[6,34],[34,34]].map(([x,y],i) => (
        <g key={i}>
          <line x1="20" y1="20" x2={x} y2={y} stroke={color} strokeWidth="1.5" opacity="0.5"/>
          <circle cx={x} cy={y} r="3.5" fill="none" stroke={color} strokeWidth="2"/>
        </g>
      ))}
    </svg>
  );
}
function ReactorIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="5" fill={color}/>
      <circle cx="20" cy="20" r="13" fill="none" stroke={color} strokeWidth="2" strokeDasharray="8 5"/>
      <circle cx="20" cy="20" r="17" fill="none" stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.6"/>
    </svg>
  );
}
function RepairIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M27 9 C25 7 21 8 19 10 L10 20 L10 24 L14 24 L23 15 C25 13 26 9 27 9 Z" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="25" y1="25" x2="31" y2="31" stroke={color} strokeWidth="3" strokeLinecap="round"/>
      <circle cx="12" cy="22" r="1.5" fill={color}/>
    </svg>
  );
}
function MfgIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <rect x="8" y="22" width="24" height="12" rx="2" fill="none" stroke={color} strokeWidth="2"/>
      <path d="M12 22 L12 12 L18 12 L18 22 M22 22 L22 8 L28 8 L28 22" stroke={color} strokeWidth="2" fill="none"/>
      <circle cx="15" cy="28" r="2" fill={color}/>
      <circle cx="25" cy="28" r="2" fill={color}/>
    </svg>
  );
}
function FusionIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="6" fill={color}/>
      <ellipse cx="20" cy="20" rx="15" ry="6" fill="none" stroke={color} strokeWidth="2" transform="rotate(30 20 20)"/>
      <ellipse cx="20" cy="20" rx="15" ry="6" fill="none" stroke={color} strokeWidth="2" transform="rotate(-30 20 20)"/>
      <ellipse cx="20" cy="20" rx="15" ry="6" fill="none" stroke={color} strokeWidth="2" transform="rotate(90 20 20)"/>
    </svg>
  );
}
function NanobotsIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      {[[20,8],[10,24],[30,24],[20,32],[10,14],[30,14]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="3" fill={color} opacity="0.9"/>
      ))}
      <line x1="20" y1="8" x2="10" y2="24" stroke={color} strokeWidth="1.5" opacity="0.6"/>
      <line x1="20" y1="8" x2="30" y2="24" stroke={color} strokeWidth="1.5" opacity="0.6"/>
      <line x1="10" y1="24" x2="30" y2="24" stroke={color} strokeWidth="1.5" opacity="0.6"/>
      <line x1="10" y1="24" x2="20" y2="32" stroke={color} strokeWidth="1.5" opacity="0.6"/>
      <line x1="30" y1="24" x2="20" y2="32" stroke={color} strokeWidth="1.5" opacity="0.6"/>
    </svg>
  );
}

function HydroIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M20 6 C20 6 10 18 10 24 a10 10 0 0 0 20 0 C30 18 20 6 20 6z" fill={color} opacity="0.7"/>
      <path d="M15 26 Q20 20 25 26" fill="none" stroke="white" strokeWidth="1.5" opacity="0.5"/>
    </svg>
  );
}
function SoilIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M8 30 Q20 26 32 30" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <path d="M20 28 C20 18 10 14 10 14 C10 14 18 20 20 28 Z" fill={color} opacity="0.85"/>
      <path d="M20 24 C20 16 28 12 28 12 C28 12 22 18 20 24 Z" fill={color} opacity="0.6"/>
    </svg>
  );
}
function MedIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <rect x="17" y="8" width="6" height="24" rx="2" fill={color} opacity="0.9"/>
      <rect x="8" y="17" width="24" height="6" rx="2" fill={color} opacity="0.9"/>
    </svg>
  );
}
function RecycleIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M20 8 L26 16 L22 16 C22 22 28 22 28 22 L26 26 C20 26 18 20 18 16 L14 16 Z" fill={color} opacity="0.9"/>
      <path d="M20 32 L14 24 L18 24 C18 18 12 18 12 18 L14 14 C20 14 22 20 22 24 L26 24 Z" fill={color} opacity="0.6"/>
    </svg>
  );
}
function WaterIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M8 28 Q14 16 20 12 Q26 16 32 28" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <ellipse cx="20" cy="29" rx="8" ry="4" fill={color} opacity="0.6"/>
      <line x1="20" y1="12" x2="20" y2="29" stroke={color} strokeWidth="1.5" opacity="0.4"/>
    </svg>
  );
}
function FoodIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <rect x="10" y="14" width="20" height="18" rx="3" fill="none" stroke={color} strokeWidth="2.5"/>
      <path d="M15 14 C15 8 25 8 25 14" stroke={color} strokeWidth="2" fill="none"/>
      <line x1="14" y1="20" x2="26" y2="20" stroke={color} strokeWidth="2" opacity="0.7"/>
      <line x1="14" y1="25" x2="22" y2="25" stroke={color} strokeWidth="2" opacity="0.7"/>
    </svg>
  );
}
function LifeIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M20 8 C12 8 8 16 8 22 C8 28 13 32 20 32 C27 32 32 28 32 22 C32 16 28 8 20 8 Z" fill="none" stroke={color} strokeWidth="2.5"/>
      <path d="M20 14 C16 18 16 22 20 26 C24 22 24 18 20 14 Z" fill={color} opacity="0.8"/>
    </svg>
  );
}
function HealthIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M6 20 L12 20 L16 12 L20 28 L24 16 L28 20 L34 20" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="20" cy="20" r="16" stroke={color} strokeWidth="1.5" fill="none" strokeDasharray="4 4" opacity="0.5"/>
    </svg>
  );
}
function BioEngIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M14 8 C26 16 26 24 14 32" stroke={color} strokeWidth="2.5" fill="none"/>
      <path d="M26 8 C14 16 14 24 26 32" stroke={color} strokeWidth="2.5" fill="none"/>
      <line x1="17" y1="13" x2="23" y2="13" stroke={color} strokeWidth="2"/>
      <line x1="20" y1="20" x2="20" y2="20" stroke={color} strokeWidth="2"/>
      <line x1="17" y1="27" x2="23" y2="27" stroke={color} strokeWidth="2"/>
    </svg>
  );
}
function EcosystemIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="22" r="12" fill="none" stroke={color} strokeWidth="2"/>
      <path d="M12 22 Q20 14 28 22" fill="none" stroke={color} strokeWidth="1.5"/>
      <path d="M20 10 C20 10 15 16 20 20 C25 16 20 10 20 10 Z" fill={color} opacity="0.8"/>
    </svg>
  );
}

function CargoIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M20 6 L24 20 L20 22 L16 20Z" fill={color} opacity="0.9"/>
      <path d="M14 20 Q20 34 20 34 Q20 34 26 20" fill="none" stroke={color} strokeWidth="2.5"/>
      <line x1="20" y1="22" x2="20" y2="34" stroke={color} strokeWidth="2" opacity="0.5"/>
    </svg>
  );
}
function CommIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M10 28 C14 20 26 20 30 28" fill="none" stroke={color} strokeWidth="2" strokeDasharray="3 2"/>
      <circle cx="20" cy="18" r="4" fill={color}/>
      <path d="M12 12 A12 12 0 0 1 28 12" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <path d="M7 7 A18 18 0 0 1 33 7" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
    </svg>
  );
}
function OxyIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="10" fill="none" stroke={color} strokeWidth="2.5"/>
      <text x="20" y="25" textAnchor="middle" fontSize="12" fill={color} fontWeight="bold">O₂</text>
      {[0,60,120,180,240,300].map((a,i) => {
        const rad=a*Math.PI/180;
        return <line key={i} x1={20+10*Math.cos(rad)} y1={20+10*Math.sin(rad)} x2={20+14*Math.cos(rad)} y2={20+14*Math.sin(rad)} stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>;
      })}
    </svg>
  );
}
function LandingIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <polygon points="20,6 26,18 14,18" fill={color} opacity="0.8"/>
      <line x1="20" y1="18" x2="20" y2="28" stroke={color} strokeWidth="2.5"/>
      <polygon points="16,26 20,32 24,26" fill={color}/>
      <line x1="8" y1="34" x2="32" y2="34" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}
function StormIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M24 6 L16 20 L22 20 L14 34 L30 18 L22 18Z" fill={color} opacity="0.9"/>
    </svg>
  );
}
function FuelIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M20 6 C20 6 11 16 11 24 C11 29 15 33 20 33 C25 33 29 29 29 24 C29 16 20 6 20 6 Z" fill="none" stroke={color} strokeWidth="2.5"/>
      <path d="M20 14 C20 14 15 20 15 25 C15 27 17 29 20 29 C23 29 25 27 25 25 C25 20 20 14 20 14 Z" fill={color} opacity="0.8"/>
    </svg>
  );
}
function AtmoIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <path d="M8 14 Q16 8 24 14 T32 14" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M8 20 Q16 14 24 20 T32 20" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
      <path d="M8 26 Q16 20 24 26 T32 26" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
    </svg>
  );
}
function SatIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <rect x="16" y="16" width="8" height="8" fill={color}/>
      <line x1="6" y1="20" x2="16" y2="20" stroke={color} strokeWidth="3"/>
      <line x1="24" y1="20" x2="34" y2="20" stroke={color} strokeWidth="3"/>
      <path d="M20 10 L20 16 M20 24 L20 30" stroke={color} strokeWidth="2"/>
      <circle cx="20" cy="8" r="2" stroke={color} strokeWidth="1.5" fill="none"/>
    </svg>
  );
}
function TerraIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="12" fill="none" stroke={color} strokeWidth="2.5"/>
      <path d="M12 20 Q16 14 20 20 T28 20" fill={color} opacity="0.5"/>
      <circle cx="20" cy="20" r="16" stroke={color} strokeWidth="1.5" fill="none" strokeDasharray="3 3" opacity="0.7"/>
    </svg>
  );
}
function OrbitalIcon({ color }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <circle cx="20" cy="20" r="15" fill="none" stroke={color} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6"/>
      <polygon points="20,8 24,18 20,16 16,18" fill={color}/>
      <polygon points="20,32 16,22 20,24 24,22" fill={color} opacity="0.7"/>
    </svg>
  );
}

const TECH_ICONS = {
  eng_solar2:     SolarIcon,
  eng_battery1:   BatteryIcon,
  eng_storage2:   StorageIcon,
  eng_heat1:      HeatIcon,
  eng_drones2:    DroneIcon,
  eng_reactor1:   ReactorIcon,
  eng_repair1:    RepairIcon,
  eng_mfg1:       MfgIcon,
  eng_fusion1:    FusionIcon,
  eng_nanobots1:  NanobotsIcon,

  bio_hydro2:     HydroIcon,
  bio_soil1:      SoilIcon,
  bio_medical:    MedIcon,
  bio_recycle1:   RecycleIcon,
  bio_water2:     WaterIcon,
  bio_foodproc1:  FoodIcon,
  bio_life1:      LifeIcon,
  bio_health1:    HealthIcon,
  bio_engineering1: BioEngIcon,
  bio_ecosystem1: EcosystemIcon,

  aero_cargo2:    CargoIcon,
  aero_comm1:     CommIcon,
  aero_oxygen2:   OxyIcon,
  aero_landing1:  LandingIcon,
  aero_storm:     StormIcon,
  aero_fuel1:     FuelIcon,
  aero_atmo1:     AtmoIcon,
  aero_sat1:      SatIcon,
  aero_terra1:    TerraIcon,
  aero_orbital1:  OrbitalIcon,
};

// ─── Hex geometry helper ───────────────────────────────────────
function hexPoints(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");
}

// ─── Hex Node ─────────────────────────────────────────────────
function HexNode({ tech, completed, activeResearch, resources, onHover, onLeave, onClick }) {
  const meta = BRANCH_META[tech.branch];
  const done = completed.includes(tech.id);
  const unlocked = isTechUnlocked(tech, completed);
  const isActive = activeResearch?.id === tech.id;
  const canAfford = Object.entries(tech.cost).every(([k, v]) => (resources[k]?.stored || 0) >= v);

  const R = 46, S = 96, cx = S/2, cy = S/2;
  const pts = hexPoints(cx, cy, R);
  const glowId = `glow-${tech.id}`;

  let fillOpacity = 0.07;
  let strokeColor = meta.color;
  let strokeOpacity = done ? 0.9 : isActive ? 0.75 : unlocked ? (canAfford ? 0.5 : 0.3) : 0.2;
  if (!unlocked) { strokeColor = "#444"; fillOpacity = 0.03; }
  else if (done) { fillOpacity = 0.2; }
  else if (isActive) { fillOpacity = 0.12; }

  const progress = isActive && activeResearch.total > 0
    ? 1 - activeResearch.remaining / activeResearch.total : 0;
  const arcR = R - 2;
  const arcLen = 2 * Math.PI * arcR;
  const arcDash = arcLen * (1 - progress);

  const Icon = TECH_ICONS[tech.id];
  const iconColor = unlocked ? meta.color : "#444";

  return (
    <div
      style={{ width: S, height: S, cursor: (unlocked && !done && !isActive && canAfford) ? "pointer" : "default", flexShrink: 0, position: "relative" }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={() => { if (unlocked && !done && !isActive && canAfford) onClick(tech); }}
    >
      <svg width={S} height={S} style={{ overflow: "visible" }}>
        <defs>
          <filter id={glowId} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation={done ? 7 : isActive ? 5 : 3} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <clipPath id={`clip-${tech.id}`}>
            <polygon points={pts}/>
          </clipPath>
        </defs>

        {/* glow bg */}
        {(done || isActive) && (
          <polygon points={pts} fill={meta.color} fillOpacity="0.1" filter={`url(#${glowId})`}/>
        )}

        {/* fill */}
        <polygon points={pts} fill={meta.color} fillOpacity={fillOpacity}/>

        {/* border */}
        <polygon points={pts} fill="none" stroke={strokeColor} strokeWidth={done || isActive ? 2.5 : 1.5} strokeOpacity={strokeOpacity}/>

        {/* progress arc */}
        {isActive && (
          <circle cx={cx} cy={cy} r={arcR} fill="none" stroke={meta.color} strokeWidth="3.5"
            strokeDasharray={arcLen} strokeDashoffset={arcDash} strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`} opacity="0.9"
            clipPath={`url(#clip-${tech.id})`}/>
        )}

        {/* icon */}
        <foreignObject x={cx-18} y={cy-20} width="36" height="36">
          <div style={{ width: 36, height: 36, opacity: unlocked ? 1 : 0.2 }}>
            {Icon && <Icon color={iconColor}/>}
          </div>
        </foreignObject>

        {/* lock */}
        {!unlocked && (
          <text x={cx} y={cy+20} textAnchor="middle" fontSize="12" fill="#555">🔒</text>
        )}

        {/* done badge */}
        {done && (
          <>
            <circle cx={cx+R*0.55} cy={cy-R*0.6} r={9} fill="#001208" stroke={meta.color} strokeWidth="1.5"/>
            <text x={cx+R*0.55} y={cy-R*0.6+4.5} textAnchor="middle" fontSize="11" fill={meta.color}>✓</text>
          </>
        )}

        {/* tier label */}
        <text x={cx-R*0.62} y={cy+R*0.68} textAnchor="middle" fontSize="9" fill={meta.color} fontFamily="monospace" opacity="0.7">T{tech.tier}</text>
      </svg>
    </div>
  );
}

// ─── Tooltip ──────────────────────────────────────────────────
function Tooltip({ tech, resources, completed, activeResearch, pos, containerRef }) {
  if (!tech) return null;
  const meta = BRANCH_META[tech.branch];
  const done = completed.includes(tech.id);
  const unlocked = isTechUnlocked(tech, completed);
  const isActive = activeResearch?.id === tech.id;
  const canAfford = Object.entries(tech.cost).every(([k, v]) => (resources[k]?.stored || 0) >= v);
  const progress = isActive && activeResearch.total > 0 ? 1 - activeResearch.remaining / activeResearch.total : 0;

  let left = pos.x + 18, top = pos.y - 10;
  if (containerRef.current) {
    const rect = containerRef.current.getBoundingClientRect();
    if (left + 290 > rect.width) left = pos.x - 308;
    if (top + 260 > rect.height) top = pos.y - 240;
    if (top < 0) top = 4;
  }

  return (
    <div style={{
      position: "absolute", left, top, zIndex: 999, width: 288, pointerEvents: "none",
      background: "linear-gradient(145deg,#06090f 70%,#0c1420)",
      border: `1px solid ${meta.color}55`,
      boxShadow: `0 0 30px ${meta.color}22, 0 6px 50px #000a`,
      fontFamily: "Rajdhani, monospace",
    }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(90deg,${meta.color}20,transparent)`, borderBottom: `1px solid ${meta.color}35`, padding: "9px 13px" }}>
        <div style={{ fontSize: 9, color: meta.color, letterSpacing: 3, textTransform: "uppercase", marginBottom: 2 }}>
          {meta.abbr} · TIER {tech.tier}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: 1 }}>{tech.name}</div>
      </div>

      <div style={{ padding: "11px 13px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Description */}
        <div style={{ fontSize: 12, color: "#ccc", lineHeight: 1.4 }}>{tech.desc}</div>

        {/* Prerequisites */}
        {tech.requires && tech.requires.length > 0 && (
          <div style={{ fontSize: 10, color: "#888", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 7 }}>
            Requires: {tech.requires.map(reqId => {
              const reqTech = TECHS.find(t => t.id === reqId);
              const met = completed.includes(reqId);
              return (
                <span key={reqId} style={{ color: met ? meta.color : "#ff4466", marginLeft: 4 }}>
                  {reqTech ? reqTech.name : reqId} {met ? "✓" : "✗"}
                </span>
              );
            })}
          </div>
        )}

        {/* Costs */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 7 }}>
          <div style={{ fontSize: 9, color: "#666", letterSpacing: 2, textTransform: "uppercase", marginBottom: 5 }}>Research Cost</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(tech.cost).map(([resKey, amt]) => {
              const res = RESOURCES[resKey];
              const have = Math.floor(resources[resKey]?.stored || 0);
              const ok = have >= amt;
              return (
                <div key={resKey} style={{
                  fontSize: 11, padding: "2px 7px", border: `1px solid ${ok ? "rgba(255,255,255,0.12)" : "#ff335566"}`,
                  background: ok ? "rgba(255,255,255,0.03)" : "#ff335510",
                  color: ok ? "#ddd" : "#ff5577", display: "flex", alignItems: "center", gap: 4,
                }}>
                  <span>{res?.icon || resKey}</span>
                  <span>{amt}</span>
                  <span style={{ fontSize: 9, opacity: 0.6 }}>({have})</span>
                </div>
              );
            })}
            <div style={{ fontSize: 11, padding: "2px 7px", border: "1px solid rgba(255,255,255,0.08)", color: "#aaa", display: "flex", alignItems: "center", gap: 4 }}>
              <span>⏱</span>
              <span>{tech.researchTime}s</span>
            </div>
          </div>
        </div>

        {/* Status */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", textAlign: "right", marginTop: 2 }}>
          {done ? (
            <span style={{ color: meta.color }}>✓ COMPLETED</span>
          ) : isActive ? (
            <span style={{ color: "#00e5ff" }}>⚡ RESEARCHING ({Math.round(progress * 100)}%)</span>
          ) : !unlocked ? (
            <span style={{ color: "#555" }}>🔒 LOCKED</span>
          ) : canAfford ? (
            <span style={{ color: "#00ff99" }}>▶ READY — CLICK TO START</span>
          ) : (
            <span style={{ color: "#ff4466" }}>INSUFFICIENT RESOURCES</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Connector arrow ──────────────────────────────────────────
function Connector({ color }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", height: 28, width: "100%" }}>
      <svg width="20" height="28" style={{ overflow: "visible" }}>
        <line x1="10" y1="2" x2="10" y2="22" stroke={color} strokeWidth="1.5" strokeOpacity="0.45" strokeDasharray="3,3"/>
        <polygon points="5,22 15,22 10,28" fill={color} opacity="0.4"/>
      </svg>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────
export default function ResearchModal() {
  const open          = useGame(s => s.researchOpen);
  const close         = useGame(s => s.closeResearch);
  const completed     = useGame(s => s.research.completed);
  const active        = useGame(s => s.research.active);
  const startResearch = useGame(s => s.startResearch);
  const resources     = useGame(s => s.resources);

  const [hoveredTech, setHoveredTech] = useState(null);
  const [tipPos, setTipPos]           = useState({ x: 0, y: 0 });
  const containerRef                  = useRef(null);

  if (!open) return null;

  const activeTechName = active ? TECHS.find(t => t.id === active.id)?.name : null;

  const branches = RESEARCH_BRANCHES.map(b => ({
    ...b,
    meta: BRANCH_META[b.id],
    techs: TECHS.filter(t => t.branch === b.id).sort((a, b) => a.tier - b.tier),
  }));

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 50,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.84)", backdropFilter: "blur(7px)",
      fontFamily: "Rajdhani, sans-serif",
    }}>
      <div
        ref={containerRef}
        onMouseMove={e => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          setTipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
        style={{
          width: "min(1060px,96vw)", height: "min(88vh,760px)",
          background: "linear-gradient(155deg,#04070e 55%,#09101e)",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 0 90px #B042FF20, 0 8px 80px #000",
          display: "flex", flexDirection: "column",
          position: "relative", overflow: "hidden",
        }}
      >
        {/* starfield */}
        <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none",opacity:0.35 }}>
          {Array.from({ length: 90 }, (_,i) => (
            <circle key={i} cx={`${(i*137.5)%100}%`} cy={`${(i*97.3)%100}%`}
              r={i%7===0 ? 1.5 : 0.7} fill="white" opacity={0.25+(i%4)*0.15}/>
          ))}
        </svg>

        {/* ── Header ── */}
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"12px 22px", borderBottom:"1px solid rgba(255,255,255,0.07)",
          background:"rgba(0,0,0,0.35)", position:"relative", zIndex:2,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:38,height:38, display:"flex",alignItems:"center",justifyContent:"center",
              border:"1px solid #B042FF", background:"#B042FF18", boxShadow:"0 0 18px #B042FF44" }}>
              <FlaskConical size={18} color="#B042FF"/>
            </div>
            <div>
              <div style={{ fontSize:9, letterSpacing:4, color:"#B042FF", textTransform:"uppercase" }}>Colony R&amp;D</div>
              <div style={{ fontSize:21, fontWeight:700, color:"#fff", letterSpacing:2, textTransform:"uppercase", lineHeight:1.1 }}>Research Tree</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:9, color:"#666", letterSpacing:3, textTransform:"uppercase" }}>Unlocked</div>
              <div style={{ fontSize:19, fontWeight:700, color:"#B042FF" }}>{completed.length} / {TECHS.length}</div>
            </div>
            <div style={{ width:96, height:4, background:"rgba(255,255,255,0.07)", borderRadius:2 }}>
              <div style={{ height:"100%", width:`${(completed.length/TECHS.length)*100}%`, background:"#B042FF", boxShadow:"0 0 10px #B042FF", borderRadius:2, transition:"width 0.4s" }}/>
            </div>
            <button onClick={close} style={{
              width:32, height:32, border:"1px solid rgba(255,255,255,0.18)", background:"rgba(255,255,255,0.04)",
              color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              transition:"border-color 0.15s, color 0.15s",
            }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="#ff3366";e.currentTarget.style.color="#ff3366";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.18)";e.currentTarget.style.color="#fff";}}
            >
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* ── Branch grid ── */}
        <div style={{ flex:1, overflowY:"auto", padding:"30px 24px", position:"relative", zIndex:1 }}>
          <div style={{ display:"flex", gap:24, justifyContent:"center" }}>
            {branches.map(branch => {
              const tierGroups = [1, 2, 3, 4, 5].map(tier => branch.techs.filter(t => t.tier === tier));
              return (
                <div key={branch.id} style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:200 }}>
                  {/* branch header */}
                  <div style={{
                    marginBottom:22, textAlign:"center", padding:"8px 18px", width:"100%",
                    border:`1px solid ${branch.meta.color}45`,
                    background:`${branch.meta.color}0e`,
                    boxShadow:`0 0 24px ${branch.meta.color}18`,
                  }}>
                    <div style={{ fontSize:9, letterSpacing:4, color:branch.meta.color, textTransform:"uppercase" }}>{branch.meta.abbr}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#fff", letterSpacing:1, textTransform:"uppercase" }}>{branch.meta.label}</div>
                    <div style={{ fontSize:10, color:"#555", marginTop:2 }}>{branch.desc}</div>
                  </div>
                  {/* tier nodes */}
                  {tierGroups.map((group, ti) => (
                    <React.Fragment key={ti}>
                      {ti > 0 && <Connector color={branch.meta.color}/>}
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        {group.map(tech => (
                          <HexNode
                            key={tech.id}
                            tech={tech}
                            completed={completed}
                            activeResearch={active}
                            resources={resources}
                            onHover={() => setHoveredTech(tech)}
                            onLeave={() => setHoveredTech(null)}
                            onClick={startResearch}
                          />
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          borderTop:"1px solid rgba(255,255,255,0.07)", padding:"8px 22px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background:"rgba(0,0,0,0.3)", position:"relative", zIndex:2,
        }}>
          <div style={{ fontSize:10, color:"#555", letterSpacing:1 }}>
            {active
              ? <span style={{ color:"#00e5ff" }}>▶ Researching: {activeTechName} — {Math.ceil(active.remaining)}s remaining</span>
              : "Hover a node for details · Click an available node to begin research"
            }
          </div>
          <div style={{ display:"flex", gap:16 }}>
            {branches.map(b => (
              <div key={b.id} style={{ display:"flex", alignItems:"center", gap:5, fontSize:10, color:"#666" }}>
                <div style={{ width:7, height:7, background:b.meta.color, boxShadow:`0 0 6px ${b.meta.color}` }}/>
                {b.meta.label}
              </div>
            ))}
          </div>
        </div>

        {/* ── Tooltip ── */}
        <Tooltip
          tech={hoveredTech}
          resources={resources}
          completed={completed}
          activeResearch={active}
          pos={tipPos}
          containerRef={containerRef}
        />
      </div>
    </div>
  );
}
